import type { UseFormReturnType } from "@dehub/theme";
import { Box, Button, Grid, Select, TextInput } from "@dehub/theme";
import { CaretDownIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import type { IBranchRow, IFormData } from "./warehouse-form";
import classes from "../../warehouse.module.css";

type Props = {
  form: UseFormReturnType<IFormData>;
  branches?: { rows?: IBranchRow[] };
  isDetail?: boolean;
  /** A branch picked over an address that already has values: waits for the user to copy it or keep the current one. */
  pendingBranch?: IBranchRow | null;
  onPendingBranchChange?: (branch: IBranchRow | null) => void;
};

const ADDRESS_FIELDS = ["province", "district", "khoroo", "khoroolol", "khotkhonBair", "khashaaDavkhar", "khaalgaDugaar", "warehouseAddress"] as const;

export function WarehouseMainSection({ form, branches, isDetail, pendingBranch, onPendingBranchChange }: Props) {
  const { t } = useTranslation("warehouse");

  const branchOptions = branches?.rows?.map((branch) => ({ label: branch.name, value: branch.id })) ?? [];

  const copyAddress = (selected: IBranchRow) => {
    form.setValues((current) => ({
      ...current,
      province: selected.province ?? "",
      district: selected.district ?? "",
      khoroo: selected.khoroo ?? "",
      khoroolol: selected.khoroolol ?? "",
      khotkhonBair: selected.khotkhonBair ?? "",
      khashaaDavkhar: selected.khashaaDavkhar ?? "",
      khaalgaDugaar: selected.khaalgaDugaar ?? "",
      warehouseAddress: selected.address ?? "",
      locationLat: selected.locationLat?.toString() ?? "",
      locationLng: selected.locationLng?.toString() ?? "",
    }));
  };

  const onBranchChange = (branchId: string | null) => {
    form.setFieldValue("branchId", branchId);
    onPendingBranchChange?.(null);
    if (!branchId) return;
    const selected = branches?.rows?.find((b) => b.id === branchId);
    if (!selected) return;
    const hasAddress = ADDRESS_FIELDS.some((key) => String(form.values[key] ?? "").trim() !== "");
    // Nothing to lose: copy straight away. Otherwise ask first.
    if (hasAddress) onPendingBranchChange?.(selected);
    else copyAddress(selected);
  };

  return (
    <>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <TextInput disabled label={t("form.orgCode")} placeholder={t("form.orgCodePlaceholder")} readOnly={isDetail} {...form.getInputProps("partnerRef")} />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <TextInput disabled label={t("form.registeredBy")} placeholder={t("form.registeredBy")} readOnly={isDetail} {...form.getInputProps("regUserName")} />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <TextInput disabled placeholder={t("form.warehouseCode")} label={t("form.warehouseCode")} readOnly={isDetail} {...form.getInputProps("refCode")} />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <TextInput label={t("form.warehouseName")} placeholder={t("form.warehouseName")} withAsterisk readOnly={isDetail} {...form.getInputProps("name")} />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <Select
          label={t("form.branch")}
          placeholder={t("form.branchPlaceholder")}
          withAsterisk
          data={branchOptions}
          clearable
          nothingFoundMessage={t("form.nothingFound")}
          rightSection={<CaretDownIcon size={16} color="gray" />}
          readOnly={isDetail}
          value={form.values.branchId}
          error={form.errors.branchId as string | undefined}
          onChange={onBranchChange}
        />
      </Grid.Col>
      {pendingBranch ? (
        <Grid.Col span={12}>
          <Box className={classes.copyNotice} role="group" aria-label={t("form.copyAddressTitle")}>
            <span>{t("form.copyAddressText", { branch: pendingBranch.name })}</span>
            <Box component="span" style={{ display: "flex", gap: 8 }}>
              <Button variant="default" size="xs" onClick={() => onPendingBranchChange?.(null)}>
                {t("form.keepAddress")}
              </Button>
              <Button
                variant="default"
                size="xs"
                onClick={() => {
                  copyAddress(pendingBranch);
                  onPendingBranchChange?.(null);
                }}>
                {t("form.copyAddress")}
              </Button>
            </Box>
          </Box>
        </Grid.Col>
      ) : null}
    </>
  );
}
